import { Toast } from '../../../../components/Common/Toast';
import { callAPI } from '../../../../services/api.service';

export type ResentStatusFilter = '' | 'resent' | 'not_resent';

export interface MarketingAuditFilters {
  email?: string | null;
  eventType?: string | null;
  fromDate?: string | null;
  toDate?: string | null;
  resentStatus?: ResentStatusFilter | null;
}

export interface MarketingAuditRecord {
  id: string;
  first_name: string | null;
  last_name: string | null;
  email: string;
  event_type: string;
  data: Record<string, any>;
  last_active: string;
  parent_first_name?: string;
  parent_last_name?: string;
  parent_email?: string;
  student_ID?: string;
  teacher_email?: string | null;
  teacher_first_name?: string;
  teacher_last_name?: string;
  project_id?: string;
  student_id?: string;
  classroom_id?: string;
  resent_at?: string;
  resent_count?: number;
}

export interface BulkResendResult {
  id: string;
  success: boolean;
  resent_at?: string;
  error?: string;
}

const buildFilterQuery = (filters: MarketingAuditFilters): string => {
  let qs = '';
  if (filters.email) qs += `&email=${encodeURIComponent(filters.email)}`;
  if (filters.eventType) qs += `&eventType=${encodeURIComponent(filters.eventType)}`;
  if (filters.fromDate) qs += `&fromDate=${encodeURIComponent(filters.fromDate + 'T00:00:00.000Z')}`;
  if (filters.toDate) qs += `&toDate=${encodeURIComponent(filters.toDate + 'T23:59:59.999Z')}`;
  if (filters.resentStatus) qs += `&resentStatus=${encodeURIComponent(filters.resentStatus)}`;
  return qs;
};

export const fetchMarketingAudits = async (
  currentPage: number,
  filters: MarketingAuditFilters = {},
  pageSize = 25
): Promise<{ audits: MarketingAuditRecord[]; totalPages: number; totalRecords: number }> => {
  try {
    const url = `admin/marketing-audits?pageNumber=${currentPage}&pageSize=${pageSize}${buildFilterQuery(filters)}`;
    const response = await callAPI(url);
    return {
      audits: response?.audits || [],
      totalPages: response?.totalPages || 0,
      totalRecords: response?.totalRecords || 0,
    };
  } catch (error) {
    Toast('Error fetching marketing audit logs', true);
    throw error;
  }
};

export const fetchMarketingAuditIds = async (filters: MarketingAuditFilters = {}): Promise<string[]> => {
  try {
    const url = `admin/marketing-audits/ids?${buildFilterQuery(filters).replace(/^&/, '')}`;
    const response = await callAPI(url);
    return response?.ids || [];
  } catch (error) {
    Toast('Error fetching marketing audit ids', true);
    throw error;
  }
};

export const resendMarketingAudit = async (
  id: string
): Promise<{ success: boolean; resent_at?: string; error?: string }> => {
  try {
    const response = await callAPI(`admin/marketing-audits/${id}/resend`, {}, 'POST');
    return {
      success: response?.success === true,
      resent_at: response?.resent_at,
      error: response?.error,
    };
  } catch (error: any) {
    return { success: false, error: error?.message || 'Unknown error' };
  }
};

// One id per request so the UI can update progress on every send. The server still
// supports batches up to 10; the client just elects to stream one at a time for
// per-request progress feedback.
export const BULK_RESEND_BATCH_SIZE = 1;

export const bulkResendMarketingAudits = async (ids: string[]): Promise<BulkResendResult[]> => {
  try {
    const response = await callAPI(`admin/marketing-audits/bulk-resend`, { ids }, 'POST');
    if (Array.isArray(response?.results)) return response.results;
    // Server returned an error envelope (e.g. { error: '...' }) or callAPI swallowed a thrown
    // error and returned null. Synthesise per-id failures so the caller sees real counts.
    const errMsg = response?.error || 'Bulk resend endpoint returned no results';
    console.error('bulkResendMarketingAudits: bad response', response);
    return ids.map((id) => ({ id, success: false, error: errMsg }));
  } catch (error: any) {
    return ids.map((id) => ({ id, success: false, error: error?.message || 'Unknown error' }));
  }
};
